/** WebAuthn/passkeys — sólo se usa si el backend habilitó la feature
 *  (producción, ver `UserOut.webauthn_available`/`HealthOut.webauthn_available`
 *  en el backend). Usa las conversiones JSON nativas del navegador (WebAuthn
 *  nivel 3: `PublicKeyCredential.parseCreationOptionsFromJSON`/
 *  `parseRequestOptionsFromJSON`/`credential.toJSON()`) en vez de escribir a
 *  mano el ida y vuelta base64url↔ArrayBuffer que exige la API vieja — evita
 *  sumar una librería sólo para eso. `as unknown as` en vez de confiar en que
 *  el lib.dom.d.ts de turno ya tenga tipados estos tres métodos (son una
 *  adición relativamente nueva del spec).
 */

type PublicKeyCredentialStatic = {
  parseCreationOptionsFromJSON: (json: unknown) => CredentialCreationOptions['publicKey']
  parseRequestOptionsFromJSON: (json: unknown) => CredentialRequestOptions['publicKey']
}

type PublicKeyCredentialWithJSON = Credential & { toJSON: () => Record<string, unknown> }

function publicKeyCredentialStatic(): PublicKeyCredentialStatic | null {
  if (typeof window === 'undefined' || !('PublicKeyCredential' in window)) return null
  const ctor = window.PublicKeyCredential as unknown as PublicKeyCredentialStatic
  if (typeof ctor.parseCreationOptionsFromJSON !== 'function') return null
  return ctor
}

export function isWebauthnSupported(): boolean {
  return publicKeyCredentialStatic() !== null
}

export async function createWebauthnCredential(
  optionsJSON: unknown,
): Promise<Record<string, unknown>> {
  const ctor = publicKeyCredentialStatic()
  if (!ctor) throw new Error('Este navegador no soporta llaves de acceso')

  const credential = (await navigator.credentials.create({
    publicKey: ctor.parseCreationOptionsFromJSON(optionsJSON),
  })) as PublicKeyCredentialWithJSON | null
  if (!credential) throw new Error('No se pudo crear la llave de acceso')
  return credential.toJSON()
}

export async function getWebauthnCredential(
  optionsJSON: unknown,
): Promise<Record<string, unknown>> {
  const ctor = publicKeyCredentialStatic()
  if (!ctor) throw new Error('Este navegador no soporta llaves de acceso')

  const credential = (await navigator.credentials.get({
    publicKey: ctor.parseRequestOptionsFromJSON(optionsJSON),
  })) as PublicKeyCredentialWithJSON | null
  if (!credential) throw new Error('No se pudo verificar la llave de acceso')
  return credential.toJSON()
}
